import React, { useCallback, useRef, useState } from "react"
import { useMutation, useQuery } from "@apollo/client/react"
import gql from "graphql-tag"
import { useNavigate, useParams } from "react-router-dom"
import { ReactFlowProvider, useEdgesState, useNodesState } from "@xyflow/react"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { useAppDispatch } from "lib/store"
import { showEditSidebar } from "lib/store/services/editSidebar/slice"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { WorkflowEdge, WorkflowNode, WorkflowNodeData } from "lib/types/workflow"
import { WorkflowApiNode } from "lib/types/workflowConditions"
import { apiJsonToFlow, flowToApiJson } from "helpers/workflowConverter"
import { extractWorkflowErrorMessage } from "helpers/workflowErrors"

import {
  CreateWorkflowDocument,
  CreateWorkflowMutation,
  CreateWorkflowMutationVariables,
  CreateWorkflowTriggerDocument,
  CreateWorkflowTriggerMutation,
  CreateWorkflowTriggerMutationVariables,
  DeleteWorkflowDocument,
  DeleteWorkflowMutation,
  DeleteWorkflowMutationVariables,
  DeleteWorkflowTriggerDocument,
  DeleteWorkflowTriggerMutation,
  DeleteWorkflowTriggerMutationVariables,
  GetWorkflowDocument,
  GetWorkflowQuery,
  GetWorkflowQueryVariables,
  OrderWorkflowTriggerType,
  SearchOrderWorkflowExecutionsDocument,
  SearchOrderWorkflowExecutionsQuery,
  SearchOrderWorkflowExecutionsQueryVariables
} from "lib/types/generated/graphql"

import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import WorkflowCanvas from "components/Workflow/Canvas/WorkflowCanvas"
import NodeEditorSidebar from "components/Workflow/NodeEditor/NodeEditorSidebar"
import DeleteWorkflow from "components/Workflow/DeleteWorkflow"
import PublishWorkflow from "components/Workflow/PublishWorkflow"
import ExecutionTable from "components/Workflow/ExecutionTable"
import RevisionSelector from "components/Workflow/RevisionSelector"
import Popup from "components/Ui/Popup"
import { BrinkLoader } from "components/Ui/BrinkLoader"
import { WithAuth } from "components/Auth"
import { PermissionGroups } from "routes/constants"

import { ReactComponent as ArrowLeft } from "images/icons/arrow-left.svg"

import {
  BackLink,
  ButtonGroup,
  CanvasSection,
  Container,
  DraftIndicator,
  Header,
  NameInput,
  ReturnToDraftButton,
  StatusBadge,
  TabsContainer
} from "./EditWorkflow.styled"

type PendingNavigation =
  | { kind: "view"; revision: number }
  | { kind: "restore"; revision: number }
  | { kind: "return-to-draft" }

export const EditWorkflow = () => {
  const { id: workflowId } = useParams<{ id: string }>()
  const [workflowName, setWorkflowName] = useState("")
  const [selectedNode, setSelectedNode] = useState<WorkflowNode | null>(null)
  const [nodes, setNodes, onNodesChange] = useNodesState<WorkflowNode>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<WorkflowEdge>([])
  const [revision, setRevision] = useState(0)
  const [publishedRevision, setPublishedRevision] = useState<number | null>(null)
  const [triggerType, setTriggerType] = useState<string>(OrderWorkflowTriggerType.OrderCreated)
  const [executionsCount, setExecutionsCount] = useState<number | null>(null)
  const [viewingRevision, setViewingRevision] = useState<number | null>(null)
  const [restoredFromRevision, setRestoredFromRevision] = useState<number | null>(null)
  const [tabIndex, setTabIndex] = useState(0)
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const initialStateRef = useRef<string>("")
  const hydratedRef = useRef(false)

  const serializeState = useCallback(
    (name: string, n: WorkflowNode[], e: WorkflowEdge[]) =>
      JSON.stringify({ name, nodes: flowToApiJson(n, e) }),
    []
  )

  const { loading: queryLoading, data } = useQuery<
    GetWorkflowQuery,
    GetWorkflowQueryVariables
  >(GetWorkflowDocument, {
    variables: { workflowId: workflowId! },
    skip: !workflowId
  })

  const { data: executionsCountData } = useQuery<
    SearchOrderWorkflowExecutionsQuery,
    SearchOrderWorkflowExecutionsQueryVariables
  >(SearchOrderWorkflowExecutionsDocument, {
    variables: {
      from: 0,
      size: 0,
      query: JSON.stringify({ bool: { must: [{ term: { workflowId: workflowId! } }] } })
    },
    skip: !workflowId,
    fetchPolicy: "network-only"
  })

  const displayedExecutionsCount =
    executionsCount ?? executionsCountData?.searchOrderWorkflowExecutions?.total

  const workflow = data?.Order?.workflow
  const revisionsList = workflow?.Revisions ?? []

  const hydrateFromDraft = useCallback(
    (
      sourceName: string,
      sourceTrigger: string,
      sourceThen: unknown,
      sourceRevision: number,
      sourcePublished: number | null
    ) => {
      setWorkflowName(sourceName)
      setRevision(sourceRevision)
      setPublishedRevision(sourcePublished)
      setTriggerType(sourceTrigger ?? OrderWorkflowTriggerType.OrderCreated)
      const apiNodes: WorkflowApiNode[] =
        typeof sourceThen === "string" ? JSON.parse(sourceThen) : ((sourceThen as WorkflowApiNode[]) ?? [])
      const flow = apiJsonToFlow(apiNodes, sourceTrigger)
      setNodes(flow.nodes)
      setEdges(flow.edges)
      initialStateRef.current = serializeState(sourceName, flow.nodes, flow.edges)
    },
    [setNodes, setEdges, serializeState]
  )

  React.useEffect(() => {
    if (!workflow || hydratedRef.current) return
    hydrateFromDraft(
      workflow.name,
      workflow.trigger,
      workflow.then,
      workflow.revision,
      workflow.published?.revision ?? null
    )
    hydratedRef.current = true
  }, [workflow, hydrateFromDraft])

  const snapshotRef = useRef<string>("")

  const [createWorkflow, { loading: saving }] = useMutation<
    CreateWorkflowMutation,
    CreateWorkflowMutationVariables
  >(CreateWorkflowDocument, {
    update: (cache, _result, { variables }) => {
      if (!variables?.workflowId) return
      const workflowCacheId = cache.identify({
        __typename: "OrderWorkflow",
        id: variables.workflowId
      })
      if (!workflowCacheId) return

      const now = new Date().toISOString()
      // AppSync's read replica is briefly inconsistent after a write, so a
      // refetch would return a stale (empty) Revisions array and clobber
      // the new entry. Write it to the cache optimistically instead.
      const newRevisionNumber = revision + 1

      const newRevisionRef = cache.writeFragment({
        data: {
          __typename: "OrderWorkflowRevision",
          id: variables.workflowId,
          revision: newRevisionNumber,
          name: variables.input.name,
          trigger: variables.input.trigger,
          created: now,
          updated: now,
          then: variables.input.then,
          published: null
        },
        fragment: gql`
          fragment NewRevisionOptimistic on OrderWorkflowRevision {
            id
            revision
            name
            trigger
            created
            updated
            then
            published {
              revision
            }
          }
        `
      })

      cache.modify({
        id: workflowCacheId,
        fields: {
          name: () => variables.input.name,
          trigger: () => variables.input.trigger,
          then: () => variables.input.then,
          revision: () => newRevisionNumber,
          updated: () => now,
          Revisions: (existing) => {
            if (!newRevisionRef) return existing
            const list = Array.isArray(existing) ? existing : []
            return [newRevisionRef, ...list]
          }
        }
      })
    },
    onCompleted: () => {
      setRevision((r) => r + 1)
      setRestoredFromRevision(null)
      initialStateRef.current = snapshotRef.current
      dispatch(
        alertActions.actions.createAlert({
          message: "Workflow saved successfully",
          type: "success"
        })
      )
    },
    onError: (error) => {
      dispatch(
        alertActions.actions.createAlert({
          message: extractWorkflowErrorMessage(error) ?? "Failed to save workflow",
          type: "error"
        })
      )
    }
  })

  const [deleteWorkflow, { loading: deleting }] = useMutation<
    DeleteWorkflowMutation,
    DeleteWorkflowMutationVariables
  >(DeleteWorkflowDocument, {
    update: (cache, _result, { variables }) => {
      if (!variables?.workflowId) return
      cache.evict({
        id: cache.identify({ __typename: "OrderWorkflow", id: variables.workflowId })
      })
      cache.evict({
        id: cache.identify({ __typename: "OrderWorkflowSearchHit", id: variables.workflowId })
      })
      cache.gc()
    },
    onCompleted: () => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Workflow deleted",
          type: "success"
        })
      )
      navigate("/settings/workflows/")
    },
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Failed to delete workflow",
          type: "error"
        })
      )
    }
  })

  const [createTrigger, { loading: publishing }] = useMutation<
    CreateWorkflowTriggerMutation,
    CreateWorkflowTriggerMutationVariables
  >(CreateWorkflowTriggerDocument, {
    onCompleted: (_result, options) => {
      const publishedRev = options?.variables?.revision ?? revision
      setPublishedRevision(publishedRev)
      dispatch(
        alertActions.actions.createAlert({
          message: "Workflow published successfully",
          type: "success"
        })
      )
    },
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Failed to publish workflow",
          type: "error"
        })
      )
    }
  })

  const [deleteTrigger] = useMutation<
    DeleteWorkflowTriggerMutation,
    DeleteWorkflowTriggerMutationVariables
  >(DeleteWorkflowTriggerDocument, {
    onCompleted: () => {
      setPublishedRevision(null)
      dispatch(
        alertActions.actions.createAlert({
          message: "Workflow unpublished",
          type: "success"
        })
      )
    },
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Failed to unpublish workflow",
          type: "error"
        })
      )
    }
  })

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: WorkflowNode) => {
      setSelectedNode(node)
      dispatch(showEditSidebar())
    },
    [dispatch]
  )

  const handleNodeSave = useCallback(
    (nodeId: string, data: WorkflowNodeData) => {
      setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data } : n)))
      setSelectedNode(null)
    },
    [setNodes]
  )

  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId))
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
      setSelectedNode(null)
    },
    [setNodes, setEdges]
  )

  const handleSave = () => {
    if (!workflowId) return
    const apiNodes = flowToApiJson(nodes, edges)
    const snapshot = serializeState(workflowName, nodes, edges)
    snapshotRef.current = snapshot

    createWorkflow({
      variables: {
        workflowId,
        input: {
          name: workflowName,
          trigger: triggerType as OrderWorkflowTriggerType,
          then: JSON.stringify(apiNodes)
        }
      }
      // No refetchQueries: the `update` callback already writes the new
      // revision, and a refetch here would race AppSync's read replica.
    })
  }

  const handleDelete = () => {
    if (!workflowId) return
    deleteWorkflow({ variables: { workflowId } })
  }

  const handlePublish = () => {
    if (!workflowId) return
    const targetRevision = viewingRevision ?? revision
    createTrigger({
      variables: { workflowId, revision: targetRevision },
      refetchQueries: [{ query: GetWorkflowDocument, variables: { workflowId } }],
      awaitRefetchQueries: true
    })
  }

  const handleUnpublish = () => {
    if (!workflowId) return
    deleteTrigger({ variables: { workflowId } })
  }

  const isPublished = publishedRevision !== null
  const hasDraft = isPublished && revision > publishedRevision
  const hasChanges =
    initialStateRef.current !== "" &&
    initialStateRef.current !== serializeState(workflowName, nodes, edges)
  const isViewingOlder = viewingRevision !== null

  const loadRevisionIntoCanvas = useCallback(
    (rev: number, options: { lockAsView: boolean }) => {
      const target = revisionsList.find((r) => r.revision === rev)
      if (!target) return
      setWorkflowName(target.name)
      setTriggerType(target.trigger ?? OrderWorkflowTriggerType.OrderCreated)
      const apiNodes: WorkflowApiNode[] =
        typeof target.then === "string"
          ? JSON.parse(target.then)
          : ((target.then as WorkflowApiNode[]) ?? [])
      const flow = apiJsonToFlow(apiNodes, target.trigger)
      setNodes(flow.nodes)
      setEdges(flow.edges)
      if (options.lockAsView) {
        setViewingRevision(rev)
        setRestoredFromRevision(null)
        initialStateRef.current = serializeState(target.name, flow.nodes, flow.edges)
      } else {
        // Leaving initialStateRef as-is lets the dirty-check flag this as unsaved.
        setViewingRevision(null)
        setRestoredFromRevision(rev)
      }
      setSelectedNode(null)
      setTabIndex(0)
    },
    [revisionsList, setNodes, setEdges, serializeState]
  )

  const returnToDraft = useCallback(() => {
    if (!workflow) return
    hydrateFromDraft(
      workflow.name,
      workflow.trigger,
      workflow.then,
      workflow.revision,
      workflow.published?.revision ?? null
    )
    setViewingRevision(null)
    setRestoredFromRevision(null)
    setSelectedNode(null)
    setTabIndex(0)
  }, [workflow, hydrateFromDraft])

  const runNavigation = useCallback(
    (nav: PendingNavigation) => {
      switch (nav.kind) {
        case "view":
          loadRevisionIntoCanvas(nav.revision, { lockAsView: true })
          break
        case "restore":
          loadRevisionIntoCanvas(nav.revision, { lockAsView: false })
          break
        case "return-to-draft":
          returnToDraft()
          break
      }
    },
    [loadRevisionIntoCanvas, returnToDraft]
  )

  const requestNavigation = useCallback(
    (nav: PendingNavigation) => {
      if (hasChanges && !isViewingOlder) {
        setPendingNavigation(nav)
        return
      }
      runNavigation(nav)
    },
    [hasChanges, isViewingOlder, runNavigation]
  )

  const handleViewRevision = useCallback(
    (rev: number) => requestNavigation({ kind: "view", revision: rev }),
    [requestNavigation]
  )

  const handleRestoreRevision = useCallback(
    (rev: number) => requestNavigation({ kind: "restore", revision: rev }),
    [requestNavigation]
  )

  const handleReturnToDraft = useCallback(
    () => runNavigation({ kind: "return-to-draft" }),
    [runNavigation]
  )

  const handleTabSelect = useCallback(
    (index: number) => {
      setTabIndex(index)
    },
    []
  )

  const confirmPendingNavigation = () => {
    if (!pendingNavigation) return
    const nav = pendingNavigation
    setPendingNavigation(null)
    runNavigation(nav)
  }

  const cancelPendingNavigation = () => setPendingNavigation(null)

  if (queryLoading) return <BrinkLoader />

  const statusLabel =
    isPublished && publishedRevision !== null
      ? `Published v${publishedRevision}`
      : isPublished
        ? "Published"
        : "Draft"

  return (
    <Container>
      <Header>
        <BackLink onClick={() => navigate("/settings/workflows/")}>
          <ArrowLeft /> Back
        </BackLink>
        <RevisionSelector
          revisions={revisionsList}
          currentDraftRevision={revision}
          publishedRevision={publishedRevision}
          viewingRevision={viewingRevision}
          restoredFromRevision={restoredFromRevision}
          onView={handleViewRevision}
          onRestore={handleRestoreRevision}
          onReturnToDraft={handleReturnToDraft}
        />
        <StatusBadge $published={isPublished && !isViewingOlder}>{statusLabel}</StatusBadge>
        {!isViewingOlder && hasDraft && (
          <DraftIndicator>v{revision} unpublished</DraftIndicator>
        )}
        {isPublished && isViewingOlder && (
          <DraftIndicator>Published: v{publishedRevision}</DraftIndicator>
        )}
        <NameInput
          aria-label="Workflow name"
          value={workflowName}
          onChange={(e) => setWorkflowName(e.target.value)}
          placeholder="Workflow name"
          maxLength={100}
          disabled={isViewingOlder}
        />
        <ButtonGroup>
          {isViewingOlder && (
            <ReturnToDraftButton type="button" onClick={handleReturnToDraft}>
              Return to draft
            </ReturnToDraftButton>
          )}
          <WithAuth requiredGroups={PermissionGroups.workflows.write}>
            {!isViewingOlder && <DeleteWorkflow onDelete={handleDelete} loading={deleting} />}
            <PublishWorkflow
              isPublished={isPublished}
              onPublish={handlePublish}
              onUnpublish={handleUnpublish}
              loading={publishing}
              hasDraft={hasDraft}
              draftRevision={revision}
              targetRevision={isViewingOlder ? viewingRevision ?? undefined : undefined}
              isOlderRevision={isViewingOlder}
              publishedRevision={publishedRevision}
            />
            {!isViewingOlder && (
              <PrimaryButton handleClick={handleSave} loading={saving} disabled={!hasChanges}>
                Save
              </PrimaryButton>
            )}
          </WithAuth>
        </ButtonGroup>
      </Header>
      <TabsContainer>
        <Tabs selectedIndex={tabIndex} onSelect={handleTabSelect}>
          <TabList>
            <Tab>Editor</Tab>
            <Tab>
              Executions
              {displayedExecutionsCount !== undefined &&
                displayedExecutionsCount !== null && (
                  <b> ({displayedExecutionsCount})</b>
                )}
            </Tab>
          </TabList>
          <TabPanel>
            <ReactFlowProvider>
              <CanvasSection>
                <WorkflowCanvas
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  setNodes={setNodes}
                  setEdges={setEdges}
                  onNodeClick={handleNodeClick}
                  readOnly={isViewingOlder}
                />
              </CanvasSection>
            </ReactFlowProvider>
          </TabPanel>
          <TabPanel>
            {workflowId && (
              <ExecutionTable
                workflowId={workflowId}
                onTotalChange={setExecutionsCount}
              />
            )}
          </TabPanel>
        </Tabs>
      </TabsContainer>
      <NodeEditorSidebar
        selectedNode={selectedNode}
        onSave={handleNodeSave}
        onDelete={handleNodeDelete}
        readOnly={isViewingOlder}
      />
      {pendingNavigation && (
        <Popup
          title="Discard unsaved changes?"
          subtitle="You have unsaved changes that will be lost. Continue?"
          handleCloseClick={(e) => {
            e.stopPropagation()
            cancelPendingNavigation()
          }}
          handleOkClick={(e) => {
            e.stopPropagation()
            confirmPendingNavigation()
          }}
          buttonText="Yes, discard"
        />
      )}
    </Container>
  )
}
